/**
 * TurnOptionsControl - Play/draw toggle and turn count for the turn simulation
 *
 * Sits beside the keep rules in the probability panel settings bar; both feed
 * the same persisted `handSimSettingsAtom`.
 */
import clsx from 'clsx';
import type { ReactElement } from 'react';

import type { TurnSimOptions } from '@/features/decks/lib/handSim';
import { SIM_TURNS_RANGE } from '@/features/decks/state/handSimSettings';

import SettingStepper from './SettingStepper';

// ============================================================================
// Types
// ============================================================================

export interface TurnOptionsControlProps {
  options: TurnSimOptions;
  onChange: (options: TurnSimOptions) => void;
  className?: string;
}

// ============================================================================
// Component
// ============================================================================

export function turnOptionsSummary(options: TurnSimOptions): string {
  return `${options.onThePlay ? 'on the play' : 'on the draw'} · ${options.turns} turns`;
}

export default function TurnOptionsControl({ options, onChange, className }: TurnOptionsControlProps): ReactElement {
  return (
    <div className={clsx('space-y-2', className)}>
      <div className="flex items-center justify-between gap-2">
        <div className="min-w-0">
          <div className="truncate text-xs text-zinc-300">Turn order</div>
          <div className="text-ash/70 truncate text-[10px]">on the play skips the turn 1 draw</div>
        </div>
        <div className="flex shrink-0 overflow-hidden rounded border border-zinc-700" role="group" aria-label="Turn order">
          <SegmentButton active={options.onThePlay} onClick={() => onChange({ ...options, onThePlay: true })}>
            Play
          </SegmentButton>
          <SegmentButton active={!options.onThePlay} onClick={() => onChange({ ...options, onThePlay: false })}>
            Draw
          </SegmentButton>
        </div>
      </div>

      <SettingStepper
        label="Turns to simulate"
        hint="how far the sample hand steps forward"
        value={options.turns}
        min={SIM_TURNS_RANGE.min}
        max={SIM_TURNS_RANGE.max}
        onChange={(turns) => onChange({ ...options, turns })}
      />
    </div>
  );
}

// ============================================================================
// Local presentation helpers
// ============================================================================

function SegmentButton({ active, onClick, children }: { active: boolean; onClick: () => void; children: string }): ReactElement {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={clsx(
        'px-2.5 py-1 text-[11px] font-medium transition-colors focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:outline-none',
        active ? 'bg-aether text-white' : 'text-zinc-400 hover:bg-zinc-800 hover:text-zinc-200',
      )}
    >
      {children}
    </button>
  );
}
